import React, { useEffect } from "react";

const SITE_NAME = "Mediit Academy";
const SITE_URL = "https://www.mediitacademy.com";
const PAGE_PATH = "/about";
const PAGE_URL = `${SITE_URL}${PAGE_PATH}`;
const LOGO_URL = `${SITE_URL}/logo.png`;
const OG_IMAGE = `${SITE_URL}/images/Class.jpg`;

const SEO_TITLE =
  "About Mediit Academy | Online LMS for JEE, NEET, Class 7 & 8";

const SEO_DESCRIPTION =
  "Mediit Academy is an online learning platform for Class 7, Class 8, JEE Mains and NEET students with live classes, notes, tests and mentor support.";

const SEO_KEYWORDS = [
  "Mediit Academy",
  "Mediit Academy LMS",
  "About Mediit Academy",
  "online learning platform India",
  "online coaching platform",
  "LMS for students",
  "best online LMS for students",
  "Class 7 online coaching",
  "Class 7 foundation course",
  "Class 8 online coaching",
  "Class 8 foundation course",
  "JEE Mains online coaching",
  "JEE foundation course",
  "NEET online coaching",
  "NEET preparation online",
  "live online classes",
  "recorded video lectures",
  "PDF study notes",
  "online test series",
  "mock test platform",
  "student dashboard",
  "teacher dashboard",
  "mentor support",
  "doubt solving online",
  "school foundation course",
  "competitive exam preparation",
].join(", ");

const focusKeywords = [
  "Mediit Academy LMS",
  "Class 7 Online Coaching",
  "Class 8 Foundation Course",
  "JEE Mains Online Coaching",
  "NEET Online Coaching",
  "Live Online Classes",
  "Recorded Video Lectures",
  "Online Test Series",
  "PDF Study Notes",
  "Mentor Doubt Support",
  "Student Dashboard",
  "Teacher Dashboard",
];

const courses = [
  {
    name: "Class 7 Foundation Course",
    description:
      "Build strong basics in Maths, Science and core school subjects with live online classes, recorded lectures, PDF notes and regular practice tests.",
    schemaDescription:
      "Online foundation course for Class 7 students with live classes, recorded lectures, notes, tests and mentor support.",
  },
  {
    name: "Class 8 Foundation Course",
    description:
      "Prepare for school exams and future competitive learning with concept-based classes, revision notes, quizzes and guided academic support.",
    schemaDescription:
      "Online foundation course for Class 8 students with live classes, recorded lectures, PDF study notes and online test series.",
  },
  {
    name: "JEE Mains Online Coaching",
    description:
      "Learn Physics, Chemistry and Mathematics with structured lessons, chapter-wise notes, mock tests, performance tracking and expert mentor guidance.",
    schemaDescription:
      "JEE Mains online preparation with expert teachers, study notes, practice questions, mock tests and performance reports.",
  },
  {
    name: "NEET Online Coaching",
    description:
      "Prepare Biology, Physics and Chemistry for NEET through live batches, recorded videos, doubt support, online tests and exam-focused study material.",
    schemaDescription:
      "NEET online preparation with Biology, Physics and Chemistry mentors, live classes, study notes and online test series.",
  },
  {
    name: "Live Online Batches",
    description:
      "Join scheduled live batches, interact with teachers, ask questions in real time and follow a regular study routine from home.",
    schemaDescription:
      "Live online batches for school foundation, JEE Mains and NEET preparation with teacher interaction and doubt support.",
  },
  {
    name: "Recorded Video Classes",
    description:
      "Revise every topic anytime with recorded video lectures, class replays, downloadable notes and dashboard-based learning access.",
    schemaDescription:
      "Recorded online video classes for students with revision support, class replays and study resources.",
  },
];

const features = [
  {
    title: "Live Interactive Classes",
    desc: "Students can attend live online classes with expert teachers, ask doubts in real time and stay consistent with a structured study schedule.",
  },
  {
    title: "Recorded Video Lectures",
    desc: "Every enrolled student can revise concepts anytime through recorded lectures, class replays and topic-wise video lessons.",
  },
  {
    title: "PDF Notes & Study Material",
    desc: "Chapter-wise PDF notes, revision material and learning resources are available inside the student dashboard for quick preparation.",
  },
  {
    title: "Online Test Series",
    desc: "Students can practice through online tests, mock exams, quizzes and performance reports for JEE, NEET and school foundation courses.",
  },
  {
    title: "Student Performance Tracking",
    desc: "Mediit Academy LMS helps students track test scores, attendance, course progress and improvement areas from one dashboard.",
  },
  {
    title: "Mentor Doubt Support",
    desc: "Dedicated mentors guide students with doubts, revision planning, preparation strategy and regular academic support.",
  },
];

const mentors = [
  {
    name: "Dr. Amit Sharma",
    role: "NEET Biology Mentor",
    image:
      "https://pinnacleorthocentre.com/wp-content/uploads/2023/05/dramit-1.jpg",
    desc: "10+ years of experience in NEET foundation, Biology concepts and medical entrance preparation.",
  },
  {
    name: "Er. Rahul Verma",
    role: "JEE Mathematics Mentor",
    image:
      "https://law.snuchennai.edu.in/wp-content/uploads/2025/05/Rahul-Verma.jpeg",
    desc: "Expert in concept-based Mathematics learning for JEE Mains and school foundation students.",
  },
  {
    name: "Priya Singh",
    role: "Class 7 & 8 Science Teacher",
    image:
      "https://psingh-research.github.io/Priya-Singh/assets/priyasinghh-DEF9rJRS.jpeg",
    desc: "Helps students build strong Science basics through simple explanations, notes, quizzes and live classes.",
  },
];

const faqs = [
  {
    question: "What is Mediit Academy LMS?",
    answer:
      "Mediit Academy LMS is an online learning management system where students can access live classes, recorded lectures, PDF notes, online test series, mentor support and performance tracking.",
  },
  {
    question: "Which courses are available on Mediit Academy?",
    answer:
      "Mediit Academy offers Class 7 foundation, Class 8 foundation, JEE Mains preparation, NEET preparation, live online batches and recorded video classes.",
  },
  {
    question: "Is Mediit Academy useful for JEE Mains and NEET preparation?",
    answer:
      "Yes, Mediit Academy provides online coaching support for JEE Mains and NEET students through expert teachers, study notes, mock tests, doubt support and structured learning paths.",
  },
  {
    question: "Can students watch recorded classes after live class?",
    answer:
      "Yes, enrolled students can watch recorded video lectures and class replays from their student dashboard after login.",
  },
  {
    question: "Does Mediit Academy provide online test series?",
    answer:
      "Yes, students get online test series, mock tests, practice questions, quizzes and performance reports for better exam preparation.",
  },
  {
    question: "Does Mediit Academy provide mentor and doubt support?",
    answer:
      "Yes, students get mentor guidance and doubt-solving support so they can improve concepts, revise better and follow a focused study plan.",
  },
];

const setMetaTag = (
  name: string,
  content: string,
  attribute: "name" | "property" = "name"
) => {
  let tag = document.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${name}"]`
  );

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const setLinkTag = (rel: string, href: string) => {
  let tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute("href", href);
};

const setJsonLd = (id: string, data: object) => {
  let script = document.querySelector<HTMLScriptElement>(`script#${id}`);

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
};

const SeoHead: React.FC = () => {
  useEffect(() => {
    document.title = SEO_TITLE;

    setMetaTag("description", SEO_DESCRIPTION);
    setMetaTag("keywords", SEO_KEYWORDS);
    setMetaTag("robots", "index, follow, max-image-preview:large");
    setMetaTag(
      "googlebot",
      "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
    );
    setMetaTag("author", SITE_NAME);
    setMetaTag("language", "English");
    setMetaTag("classification", "Education");
    setMetaTag("theme-color", "#07111b");

    setLinkTag("canonical", PAGE_URL);

    setMetaTag("og:type", "website", "property");
    setMetaTag("og:title", SEO_TITLE, "property");
    setMetaTag("og:description", SEO_DESCRIPTION, "property");
    setMetaTag("og:url", PAGE_URL, "property");
    setMetaTag("og:site_name", SITE_NAME, "property");
    setMetaTag("og:image", OG_IMAGE, "property");
    setMetaTag(
      "og:image:alt",
      "Mediit Academy online LMS platform for JEE, NEET and Class 7-8 students",
      "property"
    );
    setMetaTag("og:locale", "en_IN", "property");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", SEO_TITLE);
    setMetaTag("twitter:description", SEO_DESCRIPTION);
    setMetaTag("twitter:image", OG_IMAGE);
    setMetaTag(
      "twitter:image:alt",
      "Mediit Academy online coaching and LMS platform"
    );

    setJsonLd("mediit-about-webpage-schema", {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: SEO_TITLE,
      url: PAGE_URL,
      description: SEO_DESCRIPTION,
      inLanguage: "en-IN",
      isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
      },
      about: {
        "@type": "EducationalOrganization",
        name: SITE_NAME,
        url: SITE_URL,
        description: SEO_DESCRIPTION,
      },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: OG_IMAGE,
      },
    });

    setJsonLd("mediit-organization-schema", {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: LOGO_URL,
      description: SEO_DESCRIPTION,
      areaServed: "India",
      sameAs: [SITE_URL],
      knowsAbout: [
        "Class 7 Foundation Course",
        "Class 8 Foundation Course",
        "JEE Mains Preparation",
        "NEET Preparation",
        "Online Test Series",
        "Live Online Classes",
        "Recorded Video Lectures",
        "PDF Study Notes",
        "Learning Management System",
      ],
    });

    setJsonLd("mediit-course-itemlist-schema", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Mediit Academy Online Courses",
      itemListElement: courses.slice(0, 4).map((course, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Course",
          name: course.name,
          description: course.schemaDescription,
          provider: {
            "@type": "EducationalOrganization",
            name: SITE_NAME,
            url: SITE_URL,
          },
        },
      })),
    });

    setJsonLd("mediit-about-breadcrumb-schema", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: SITE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "About Mediit Academy",
          item: PAGE_URL,
        },
      ],
    });

    setJsonLd("mediit-about-faq-schema", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
  }, []);

  return null;
};

const About: React.FC = () => {
  return (
    <main className="w-full bg-slate-50 text-slate-900 font-['Inter']">
      <SeoHead />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#07111b] via-[#17172f] to-[#3b2418] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="inline-block mb-4 rounded-full bg-white/10 px-4 py-2 text-sm font-medium">
              About Mediit Academy Online LMS Platform
            </p>

            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              Online LMS Platform for Class 7, Class 8, JEE Mains & NEET
              Preparation
            </h1>

            <p className="mt-6 text-lg text-slate-200 leading-8">
              Mediit Academy is a complete online coaching and learning
              management system for Class 7, Class 8, JEE Mains and NEET
              students. Our LMS provides live online classes, recorded video
              lectures, PDF study notes, online test series, expert mentor
              guidance and student performance tracking in one student
              dashboard.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/courses"
                aria-label="Explore Mediit Academy online courses for Class 7, Class 8, JEE and NEET"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition"
              >
                Explore Courses
              </a>

              <a
                href="/talk-to-counselor"
                aria-label="Talk to Mediit Academy counselor for course guidance"
                className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-semibold transition"
              >
                Talk to Counselor
              </a>
            </div>
          </div>

          <div className="relative lg:translate-x-12 xl:translate-x-20">
            <img
              src="https://www.iitms.co.in/images/Learning-management-system-info.png"
              alt="Mediit Academy LMS platform for live online classes, recorded lectures, student dashboard, teachers and mentors"
              title="Mediit Academy Online LMS Platform for Students"
              loading="eager"
              decoding="async"
              className="w-full h-[420px] object-contain"
            />

            <div className="absolute -bottom-6 left-0 bg-white text-slate-900 rounded-2xl shadow-xl p-5 hidden md:block">
              <p className="text-2xl font-bold">5000+</p>
              <p className="text-sm text-slate-600">
                Students Learning Online
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section
        className="max-w-7xl mx-auto px-6 -mt-8 relative z-10"
        aria-label="Mediit Academy LMS achievements and online coaching stats"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {[
            ["50+", "Expert Teachers"],
            ["100+", "Live Classes"],
            ["500+", "Study Notes"],
            ["24/7", "Student Support"],
          ].map(([number, label]) => (
            <article
              key={label}
              className="bg-white rounded-2xl shadow-md p-6 text-center"
            >
              <p className="text-3xl font-bold text-orange-500">{number}</p>
              <h2 className="mt-2 text-slate-600 font-medium">{label}</h2>
            </article>
          ))}
        </div>
      </section>

      {/* Who We Are */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <img
            src="https://mediitacademy.com/images/Class.jpg"
            alt="Mediit Academy classroom for online coaching, school foundation classes, JEE Mains and NEET preparation"
            title="Mediit Academy Classroom and Online Coaching"
            loading="lazy"
            decoding="async"
            className="rounded-3xl shadow-lg w-full h-[420px] object-cover"
          />
        </div>

        <div>
          <p className="text-orange-500 font-semibold">Who We Are</p>

          <h2 className="mt-3 text-3xl md:text-4xl font-bold">
            Complete Digital Learning System for Modern Education
          </h2>

          <p className="mt-5 text-slate-600 leading-8">
            Mediit Academy is an online coaching platform designed for school
            foundation courses, JEE Mains preparation and NEET preparation. Our
            LMS connects students, teachers and mentors on one digital learning
            platform where students can attend live classes, watch recorded
            lessons, download PDF study notes, attempt online tests and track
            academic progress.
          </p>

          <p className="mt-4 text-slate-600 leading-8">
            Teachers can manage online classes, upload study materials, create
            test series and guide students with a structured learning path.
            Admin can manage courses, students, teachers, payments, reports,
            live batches and LMS settings from a single admin dashboard.
          </p>
        </div>
      </section>

      {/* Courses */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-orange-500 font-semibold">
              Student Classes & Online Courses
            </p>

            <h2 className="mt-3 text-3xl md:text-4xl font-bold">
              Courses Available on Mediit Academy LMS
            </h2>

            <p className="mt-4 text-slate-600 leading-8">
              Students can choose their class or competitive exam course and
              access live classes, recorded videos, notes, practice tests and
              mentor support after login.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <article
                key={course.name}
                className="rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition bg-slate-50"
              >
                <div
                  className="h-12 w-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center text-xl font-bold"
                  aria-hidden="true"
                >
                  ✓
                </div>

                <h3 className="mt-5 text-xl font-bold">{course.name}</h3>

                <p className="mt-3 text-slate-600 leading-7">
                  {course.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* LMS Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-orange-500 font-semibold">LMS Features</p>

          <h2 className="mt-3 text-3xl md:text-4xl font-bold">
            Everything Students Need in One Online Learning Dashboard
          </h2>

          <p className="mt-4 text-slate-600 leading-8">
            Mediit Academy LMS gives students a complete digital classroom
            experience with online coaching tools, learning resources, exam
            preparation support and performance tracking.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <article
              key={feature.title}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6"
            >
              <h3 className="text-xl font-bold">{feature.title}</h3>

              <p className="mt-3 text-slate-600 leading-7">
                {feature.desc}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Mentors */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-orange-400 font-semibold">
              Our Mentors & Teachers
            </p>

            <h2 className="mt-3 text-3xl md:text-4xl font-bold">
              Learn from Experienced JEE, NEET and Foundation Teachers
            </h2>

            <p className="mt-4 text-slate-300 leading-8">
              Our expert teachers focus on concept clarity, regular practice,
              doubt-solving, exam-oriented preparation and personal mentor
              guidance for every enrolled student.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {mentors.map((mentor) => (
              <article
                key={mentor.name}
                className="bg-white/10 rounded-3xl p-5 backdrop-blur border border-white/10"
              >
                <img
                  src={mentor.image}
                  alt={`${mentor.name}, ${mentor.role} at Mediit Academy online coaching platform`}
                  title={`${mentor.name} - ${mentor.role}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-72 object-cover rounded-2xl"
                />

                <h3 className="mt-5 text-xl font-bold">{mentor.name}</h3>

                <p className="mt-1 text-orange-400 font-medium">
                  {mentor.role}
                </p>

                <p className="mt-3 text-slate-300 leading-7">
                  {mentor.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Process */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-orange-500 font-semibold">Learning Process</p>

          <h2 className="mt-3 text-3xl md:text-4xl font-bold">
            How Students Learn on Mediit Academy LMS
          </h2>

          <p className="mt-4 text-slate-600 leading-8">
            Our online LMS platform makes the student learning journey simple,
            structured and result-focused from registration to performance
            improvement.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-4 gap-6">
          {[
            ["01", "Register & Login"],
            ["02", "Choose Course"],
            ["03", "Attend Classes"],
            ["04", "Track Progress"],
          ].map(([step, title]) => (
            <article
              key={title}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
            >
              <p className="text-4xl font-bold text-orange-500">{step}</p>

              <h3 className="mt-4 text-xl font-bold">{title}</h3>

              <p className="mt-3 text-slate-600 leading-7">
                Students get a complete LMS experience from online admission and
                course enrollment to live learning, test practice and performance
                improvement.
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Focus Keywords */}
      <section className="bg-slate-100 py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-orange-500 font-semibold">Popular Learning Searches</p>

          <h2 className="mt-3 text-3xl md:text-4xl font-bold">
            Online Coaching, LMS and Exam Preparation 
          </h2>

          <p className="mt-4 text-slate-600 leading-8 max-w-3xl mx-auto">
            Mediit Academy focuses on online coaching, school foundation
            learning, JEE Mains preparation, NEET preparation, live online
            classes, recorded lectures, study notes and online test series for
            students across India.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {focusKeywords.map((keyword) => (
              <span
                key={keyword}
                className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm border border-slate-200"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SEO FAQ Section */}
      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <p className="text-orange-500 font-semibold">
              Frequently Asked Questions
            </p>

            <h2 className="mt-3 text-3xl md:text-4xl font-bold">
              FAQs About Mediit Academy Online LMS
            </h2>

            <p className="mt-4 text-slate-600 leading-8">
              Find answers about Mediit Academy LMS, online coaching courses,
              live classes, recorded lectures, PDF notes and online test series.
            </p>
          </div>

          <div className="mt-12 space-y-5">
            {faqs.map((faq) => (
              <article
                key={faq.question}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
              >
                <h3 className="text-xl font-bold text-slate-900">
                  {faq.question}
                </h3>

                <p className="mt-3 text-slate-600 leading-7">
                  {faq.answer}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
